import React, { useEffect, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Box, Button, Modal, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import doneAnime from "../../../assets/anime/done.json";
import Lottie from "react-lottie";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { lg: 350, md: 350, sm: 250, xs: 280 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  overflowY: "scroll",
  maxHeight: "80vh",
};

const CardInput = ({ amount, onPaymentSuccess, onPaymentError, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
    const { enqueueSnackbar } = useSnackbar();

    const [openModal, setOpenModal] = useState(false)

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
    const handleAlert = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [isPaymentSuccessful, setPaymentSuccessful] = useState(false)
  const [successPaymentIntent, setSuccessPaymentIntent] = useState(null)

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          handleAlert("Payment successful!", "success");
         onPaymentSuccess()
          // onClose()
          setOpenModal(true)
          break;
        case "processing":
          setMessage("Your payment is processing.");
          handleAlert("Your payment is processing.", "info");
          break;
        case "requires_payment_method":
          handleAlert(`Your payment was not successful, please try again.`, "error")
          setMessage("Your payment was not successful, please try again.");
          // Call error callback if provided
          if (onPaymentError) {
            onPaymentError(new Error("Payment was not successful"));
          }
          break;
        default:
          setMessage("Something went wrong.");
          handleAlert("Something went wrong.", "error");
          // Call error callback if provided
          if (onPaymentError) {
            onPaymentError(new Error("Something went wrong"));
          }
          break;
      }
    }).catch((error) => {
      console.error("Error retrieving payment intent:", error);
      handleAlert("Error retrieving payment status", "error");
      if (onPaymentError) {
        onPaymentError(error);
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    // Use confirmPayment without redirect to handle success in-place
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required', // Only redirect if absolutely necessary
    });

    if (error) {
      // Payment failed or was cancelled
      if (error.type === "card_error" || error.type === "validation_error") {
        handleAlert(`${error.message}`, "error");
        setMessage(error.message);
      } else {
        handleAlert("An unexpected error occurred.", "error");
        setMessage("An unexpected error occurred.");
      }
      
      // Call error callback if provided
      if (onPaymentError) {
        onPaymentError(error);
      }
    } else if (paymentIntent) {
      // Payment succeeded
      if (paymentIntent.status === 'succeeded') {
        setMessage("Payment succeeded!");
        handleAlert("Payment successful!", "success");
       onPaymentSuccess()
        onClose()
        setOpenModal(true)
        // Don't call onPaymentSuccess here - let the modal button handle it
      } else if (paymentIntent.status === 'processing') {
        setMessage("Your payment is processing.");
        handleAlert("Your payment is processing.", "info");
      } else {
        setMessage(`Payment status: ${paymentIntent.status}`);
        handleAlert(`Payment status: ${paymentIntent.status}`, "info");
      }
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs",
  };

    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: doneAnime,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid slice",
      },
    };

  return (
    <>
    <form id="payment-form" onSubmit={handleSubmit}>
      {!stripe && !elements && (
        <Box align="center">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/b/b1/Loading_icon.gif"
            width={150}
          />
        </Box>
      )}
      {elements && (
        <PaymentElement id="payment-element" options={paymentElementOptions} />
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          disabled={isLoading || !stripe || !elements}
          onClick={handleSubmit}
          variant="contained"
          fullWidth
        >{`Pay Now $${amount}`}</Button>
      </Box>

      {/* Show any error or success messages */}
      {message && <Typography id="payment-message" >{message}</Typography>}
    </form>

    <Modal open={openModal} onClose={()=>setOpenModal(false)}>
      <Box sx={style}>
        <Box sx={{
          bgcolor: '#fff', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 3
        }}>
          <Lottie options={defaultOptions} width={120} height={120} />
          
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: '#2E1834', 
              mt: 2, 
              mb: 1 
            }}
          >
            Payment Successful!
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666', 
              mb: 3,
              fontSize: '14px'
            }}
          >
            ${amount} has been added to your Mamapurse
          </Typography>
          
          <Button 
            onClick={()=>{

              
              if (onPaymentSuccess && successPaymentIntent) {

                onPaymentSuccess(successPaymentIntent);
              } else {

              }
              setOpenModal(false);
            }}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: "100px",
              backgroundColor: "#2E1834",
              py: 1.5,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#1a0f1f",
              },
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
};

export default CardInput;
